import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, useParams } from 'react-router-dom';
import { useSelector } from '../../reduxHooks';
import { Alert, Button, Card, Dialog } from '@betareader/design-system';

import {
  McpReplyDraft,
  cancelMcpReplyDraft,
  editMcpReplyDraft,
  getMcpReplyDraft,
  sendMcpReplyDraft,
} from '../../services/mcpApi';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import {
  canOpenMcpIntegrationSettings,
  isMcpFeatureAvailable,
} from './integrations';

type ReplyDraftViewProps = {
  currentUserId?: string;
  draft?: McpReplyDraft;
  loading: boolean;
  busy: boolean;
  error?: string;
  onSave: (text: string) => void;
  onCancel: () => void;
  onSend: (version: number) => void;
};

export const ReplyDraftApprovalView: React.FC<ReplyDraftViewProps> = ({
  currentUserId,
  draft,
  loading,
  busy,
  error,
  onSave,
  onCancel,
  onSend,
}) => {
  const { t } = useTranslation();
  const [text, setText] = useState(draft?.text ?? '');
  const [confirmOpen, setConfirmOpen] = useState(false);
  useEffect(() => setText(draft?.text ?? ''), [draft?.text, draft?.version]);
  useEffect(
    () => setConfirmOpen(false),
    [draft?.id, draft?.version, draft?.status]
  );
  if (loading) return <p role="status">{t('Loading', 'Loading…')}</p>;
  const changed = Boolean(draft && text !== draft.text);
  const recipients =
    draft?.conversation.participants.filter(
      person => person.id !== currentUserId
    ) ?? [];

  return (
    <main style={{ maxWidth: 720, margin: '0 auto', padding: 16 }}>
      <Card>
        <h1>{t('McpReviewReply', 'Review reply')}</h1>
        {error && <Alert variant="error">{error}</Alert>}
        {draft && (
          <>
            <p>
              <strong>{t('Conversation')}:</strong>{' '}
              <span>{draft.conversation.subject ?? t('Conversation')}</span>
            </p>
            <p>
              <strong>{t('McpTo', 'To')}:</strong>{' '}
              {recipients
                .map(person => person.displayName ?? person.id)
                .join(', ')}
            </p>
            <div>
              <label htmlFor="mcp-reply-text">
                {t('McpReplyText', 'Reply text')}
              </label>
              <textarea
                id="mcp-reply-text"
                className="br-field__input"
                value={text}
                maxLength={20000}
                rows={8}
                disabled={busy || draft.status !== 'draft'}
                onChange={event => setText(event.currentTarget.value)}
              />
            </div>
            {draft.status === 'sent' ? (
              <Alert variant="success">
                {t('McpReplySent', 'Reply sent.')}
              </Alert>
            ) : draft.status === 'cancelled' ? (
              <Alert variant="info">
                {t('McpReplyCancelled', 'Draft cancelled.')}
              </Alert>
            ) : (
              <>
                <Alert variant="info">
                  {t('McpNotSent', 'This message has not been sent.')}
                </Alert>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 8,
                    marginTop: 16,
                  }}
                >
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={busy}
                    onClick={onCancel}
                  >
                    {t('Cancel')}
                  </Button>
                  {changed && (
                    <Button
                      type="button"
                      disabled={busy || text.length === 0}
                      onClick={() => onSave(text)}
                    >
                      {t('McpSaveChanges', 'Save changes')}
                    </Button>
                  )}
                  <Button
                    type="button"
                    disabled={busy || changed}
                    aria-busy={busy}
                    onClick={() => setConfirmOpen(true)}
                  >
                    {t('McpSendReply', 'Send reply')}
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      </Card>
      <Dialog
        open={confirmOpen && !!draft && draft.status === 'draft'}
        title={t('McpSendConfirmation', 'Send this exact reply now?')}
        onClose={() => {
          if (!busy) setConfirmOpen(false);
        }}
        actions={
          <>
            <Button
              variant="secondary"
              disabled={busy}
              onClick={() => setConfirmOpen(false)}
            >
              {t('Cancel', 'Cancel')}
            </Button>
            <Button
              disabled={busy}
              aria-busy={busy}
              onClick={() => {
                if (draft) onSend(draft.version);
                setConfirmOpen(false);
              }}
            >
              {t('McpConfirmSendReply', 'Confirm send')}
            </Button>
          </>
        }
      >
        <p>
          {t(
            'McpConfirmExactReply',
            'This sends the exact reply shown above to the conversation.'
          )}
        </p>
      </Dialog>
    </main>
  );
};

const ReplyDraftApproval: React.FC = () => {
  const { draftId } = useParams<{ draftId: string }>();
  const userProfile = useSelector((state: any) => state.user.userProfile);
  const activeAccount = useSelector(
    (state: any) => state.account.activeAccount
  );
  const { flags, isLoading: flagsLoading } = useFeatureFlags();
  const featureEnabled = isMcpFeatureAvailable(
    flags,
    flagsLoading,
    userProfile
  );
  const canOpenSettings =
    featureEnabled && canOpenMcpIntegrationSettings(userProfile, activeAccount);
  const [draft, setDraft] = useState<McpReplyDraft>();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    let current = true;
    if (!canOpenSettings) {
      setLoading(false);
      return () => {
        current = false;
      };
    }
    getMcpReplyDraft(draftId)
      .then(value => {
        if (current) setDraft(value);
      })
      .catch(reason => {
        if (current) setError(reason.message);
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => {
      current = false;
    };
  }, [canOpenSettings, draftId]);

  const perform = async (action: () => Promise<any>) => {
    if (busy) return;
    setBusy(true);
    setError(undefined);
    try {
      const result = await action();
      if (result) setDraft(result);
    } catch (reason: any) {
      setError(reason.message);
    } finally {
      setBusy(false);
    }
  };

  if (flagsLoading || !userProfile) return <p role="status">Loading…</p>;
  if (!canOpenSettings) return <Redirect to="/account/settings" />;

  return (
    <ReplyDraftApprovalView
      currentUserId={userProfile?._id}
      draft={draft}
      loading={loading}
      busy={busy}
      error={error}
      onSave={text => perform(() => editMcpReplyDraft(draftId, text))}
      onCancel={() =>
        perform(async () => {
          await cancelMcpReplyDraft(draftId);
          return draft ? { ...draft, status: 'cancelled' as const } : undefined;
        })
      }
      onSend={version =>
        perform(async () => {
          await sendMcpReplyDraft(draftId, version);
          return draft ? { ...draft, status: 'sent' as const } : undefined;
        })
      }
    />
  );
};

export default ReplyDraftApproval;
